// src/pages/ContactPage.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import mascotImage from '../assets/images/test-img-01.webp';
import landingBackgroundImg from '../assets/images/Landing.webp';
import Button from '../components/common/Button';

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(${landingBackgroundImg}) no-repeat center center;
    background-size: cover;
    z-index: -2;
  }

  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 1;
`;

const FixedNavigation = styled.nav`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  gap: 2rem;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 12px;
  backdrop-filter: blur(10px);
`;

// New flexible container for layout
const FlexContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  align-items: flex-start;
  justify-content: space-between;
`;

// Left column for form
const FormColumn = styled.div`
  flex: 1;
  min-width: 300px;
  max-width: 600px;
`;

// Right column for mascot
const MascotColumn = styled.div`
  flex: 1;
  min-width: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  
  @media (max-width: 1024px) {
    order: -1; // Places mascot above form on mobile
    width: 100%;
    padding: 0 0 2rem 0;
  }
`;

const MascotImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
`;

const PageHeading = styled.h1`
  margin-bottom: 1.5rem;
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
`;

const ContactDescription = styled.p`
  font-size: 1.1rem;
  max-width: 600px;
  margin-bottom: 2.5rem;
  color: #ffffff;
`;

const ContactForm = styled.form`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #ffffff;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.colors.backgroundAlt};
  border-radius: ${props => props.theme.borderRadius};
  font-family: inherit;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.colors.backgroundAlt};
  border-radius: ${props => props.theme.borderRadius};
  font-family: inherit;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: border-color 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const ErrorMessage = styled.p`
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled(motion.div)`
  background-color: #c6f6d5;
  color: #276749;
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: 1.5rem;
`;

// Interface for form data to ensure type safety
interface FormData {
  name: string;
  email: string;
  message: string;
  honeypot?: string;
}

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 1em;
  height: 1em;
  margin-left: 0.5rem;
  border: 0.2em solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ScrollToTopLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <NavLink to={to} onClick={handleClick}>
      {children}
    </NavLink>
  );
};

const ContactPage: React.FC = () => {
  const { 
    register, 
    handleSubmit, 
    reset,
    formState: { errors, isSubmitting } 
  } = useForm<FormData>();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (data: FormData) => {
    try {
      if (data.honeypot) {
        return;
      }

      // Add your form submission logic here
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulated API call
      setSubmitSuccess(true);
      setSubmitError(null);
      reset();
    } catch (error) {
      setSubmitError('An error occurred while sending your message. Please try again.');
      setSubmitSuccess(false);
    }
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <FixedNavigation>
          <ScrollToTopLink to="/">HOME</ScrollToTopLink>
          <ScrollToTopLink to="/about">ABOUT</ScrollToTopLink>
          <ScrollToTopLink to="/services">SERVICES</ScrollToTopLink>
          <ScrollToTopLink to="/education">EDUCATION</ScrollToTopLink>
        </FixedNavigation>

        <ContactSection>
          <PageHeading>Get in Touch</PageHeading>
          <ContactDescription>
            Have a vision for the future of your industry? Want to discuss a potential project or venture? Fill out the form below
            and let's forge a new path forward together.
          </ContactDescription>
          
          <FlexContainer>
            <FormColumn>
              {submitSuccess && (
                <SuccessMessage
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  Your message has been sent successfully! I'll get back to you soon.
                </SuccessMessage>
              )}
              
              {submitError && (
                <ErrorMessage>{submitError}</ErrorMessage>
              )}
              
              <ContactForm onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                  {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    {...register('message', { required: 'Message is required' })}
                  />
                  {errors.message && <ErrorMessage>{errors.message.message}</ErrorMessage>}
                </FormGroup>

                <input
                  type="text"
                  {...register('honeypot')}
                  style={{ display: 'none' }}
                />

                <Button 
                  type="submit" 
                  primary 
                  large
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      Sending
                      <LoadingSpinner aria-hidden="true" />
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </ContactForm>
            </FormColumn>
            
            <MascotColumn>
              <MascotImage 
                src={mascotImage} 
                alt="TAP Construction Mascot" 
                width="400"
                height="400"
              />
            </MascotColumn>
          </FlexContainer>
        </ContactSection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ContactPage;